'use client';

import { Input, Label } from '@/components/elements';
import { register } from '@/lib/auth';
import { useAuthModal } from '@/lib/context/AuthModalContext';
import { useRouter } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { ChangeEvent, FormEvent, useMemo, useState } from 'react';

interface IInputFields {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  passwordConfirm: string;
  isAgency: boolean;
}

const initialInputs = { firstName: '', lastName: '', email: '', password: '', passwordConfirm: '', isAgency: false };
const initialErrors = {
  firstName: false,
  lastName: false,
  email: false,
  password: false,
  passwordConfirm: false,
  isAgency: false,
};

interface IMobileRegisterFormProps {
  switchForm: () => void;
  handleClose: () => void;
}

export const MobileRegisterForm = ({ switchForm, handleClose }: IMobileRegisterFormProps) => {
  const router = useRouter();
  const { userType } = useAuthModal();
  const [errors, setErrors] = useState<Record<keyof IInputFields, boolean>>(initialErrors);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputs, setInputs] = useState<IInputFields>(() => ({ ...initialInputs, isAgency: false }));

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputs((prevValue) => ({ ...prevValue, [e.target.name]: e.target.value }));
    setErrors((prevValue) => ({ ...prevValue, [e.target.name]: false }));
    setErrorMessage(null);
    if (e.target.name === 'password' && inputs.passwordConfirm !== '')
      checkMatch(inputs.passwordConfirm, e.target.value, 'passwordConfirm');
    if (e.target.name === 'passwordConfirm' && inputs.passwordConfirm !== '')
      checkMatch(e.target.value, inputs.password, 'passwordConfirm');
  };

  const checkMatch = (firstValue: string, secondValue: string, key: string) => {
    setErrors((prevValue) => ({ ...prevValue, [key]: firstValue !== secondValue }));
  };

  const isDisabled = useMemo(
    () =>
      !inputs.firstName.length ||
      !inputs.lastName.length ||
      !inputs.email.length ||
      !inputs.password.length ||
      !inputs.passwordConfirm.length ||
      inputs.password !== inputs.passwordConfirm,
    [inputs]
  );

  const validateForm = () => {
    let hasErrors = false;
    const newErrors = { ...initialErrors };

    if (!inputs.firstName.trim()) {
      newErrors.firstName = true;
      hasErrors = true;
    }

    if (!inputs.lastName.trim()) {
      newErrors.lastName = true;
      hasErrors = true;
    }

    if (!inputs.email || !inputs.email.includes('@')) {
      newErrors.email = true;
      hasErrors = true;
    }

    if (!inputs.password || inputs.password.length < 6) {
      newErrors.password = true;
      hasErrors = true;
    }

    if (inputs.password !== inputs.passwordConfirm) {
      newErrors.passwordConfirm = true;
      hasErrors = true;
    }

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage(null);

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    const { passwordConfirm, ...payload } = inputs;

    try {
      const result = await register(payload);

      if ('success' in result && !result.success) {
        // Registration failed with a known error from backend
        setErrorMessage(result?.message || '');
        if (result?.message?.toLowerCase().includes('password')) setErrors((prev) => ({ ...prev, password: true }));
        if (result?.message?.toLowerCase().includes('first name')) setErrors((prev) => ({ ...prev, firstName: true }));
        if (result?.message?.toLowerCase().includes('last name')) setErrors((prev) => ({ ...prev, lastName: true }));
        if (result?.message?.toLowerCase().includes('email')) setErrors((prev) => ({ ...prev, email: true }));
      } else {
        // Registration succeeded, auto sign in
        const signInResult = await signIn("credentials", {
          email: inputs.email,
          password: inputs.password,
          redirect: false,
        });

        if (signInResult?.error) {
          // Registration succeeded but auto-login failed, not critical
          console.error('Auto-login after registration failed:', signInResult.error);
          setErrorMessage('Account created! Please sign in.');
          return;
        }

        handleClose();
        if (userType === 'provider') router.push('/register-tour-operater');
        router.refresh();
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      setErrorMessage(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="px-8 flex flex-col gap-16">
        <div className="profile-modal__header relative">
          <h2 className="text-2xl">Create account</h2>
        </div>

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{errorMessage}</div>
        )}

        <div className="flex flex-col gap-10">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Label>First name</Label>
                <Input.Controlled
                  name="firstName"
                  value={inputs.firstName}
                  handleChange={handleChange}
                  placeholder="Enter"
                />
                {errors.firstName && <p className="text-error-500 mb-4">First name is required</p>}
              </div>

              <div className="flex flex-col gap-1">
                <Label>Last name</Label>
                <Input.Controlled
                  name="lastName"
                  value={inputs.lastName}
                  handleChange={handleChange}
                  placeholder="Enter"
                />
                {errors.lastName && (
                  <p className="text-error-500 mb-3">
                    {errorMessage?.toLowerCase().includes('last name') ? 'Invalid last name.' : 'Required last name.'}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <Label>Email address</Label>
                <Input.Controlled name="email" value={inputs.email} handleChange={handleChange} placeholder="Enter" />
                {errors.email && <p className="text-error-500 mb-3">Please enter a valid email address</p>}
              </div>

              <div className="flex flex-col gap-1">
                <Label>Create password</Label>
                <Input.Password
                  name="password"
                  value={inputs.password}
                  handleChange={handleChange}
                  placeholder="Enter"
                />
                {errors.password && <p className="text-error-500 mb-3">Password must be at least 6 characters</p>}
              </div>

              <div className="flex flex-col gap-1">
                <Label>Confirm password</Label>
                <Input.Password
                  name="passwordConfirm"
                  value={inputs.passwordConfirm}
                  handleChange={handleChange}
                  placeholder="Enter"
                />
                {errors.passwordConfirm && <p className="text-error-500 mb-3">Passwords do not match</p>}
              </div>
            </div>
          </div>

          <button
            className={`btn w-full ${isLoading || isDisabled ? 'disabled' : ''}`}
            onClick={handleSubmit}
            disabled={isLoading || isDisabled}
          >
            {isLoading ? 'Creating...' : 'Create account'}
          </button>
        </div>
      </div>
      <div className="sticky lg:relative h-20 px-8 pb-2 lg:pb-12 filter-modal__footer border-gray-800 bottom-0 text-center">
        <span>
          Already a member?{' '}
          <span className="font-semibold cursor-pointer" onClick={switchForm}>
            Sign in
          </span>
        </span>
      </div>
    </>
  );
};